import React, { useState } from 'react';
import { Button } from './ui/button';
import { MapPin, Clock, User, CheckCircle, Navigation, Activity } from 'lucide-react';
import { mockRides, dashboardStats } from '../mock';
import DemoRequestModal from './DemoRequestModal';

const AppPreviewSection = () => {
  const [selectedRide, setSelectedRide] = useState(mockRides[1]);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-[#2ECC71]';
      case 'en-route': return 'bg-[#0077BE]';
      case 'scheduled': return 'bg-[#00B4D8]';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'completed': return 'Completed';
      case 'en-route': return 'En Route';
      case 'scheduled': return 'Scheduled';
      default: return status;
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-[#0B0F14] via-[#00B4D8] to-[#0077BE] relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-6">
            Real-Time Visibility. Built for Peace of Mind.
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
            Introducing our upcoming tracking platform—providing families and districts with 
            transparency, communication, and real-time updates.
          </p>
        </div>

        {/* Mock Dashboard */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Stats Cards */}
          <div className="lg:col-span-1 space-y-4">
            <div className="p-6 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white/80 text-sm font-semibold">Total Rides Today</p>
                <Activity className="w-5 h-5 text-white/60" />
              </div>
              <p className="text-4xl font-bold text-white">{dashboardStats.totalRides}</p>
            </div>
            
            <div className="p-6 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white/80 text-sm font-semibold">Active Routes</p>
                <MapPin className="w-5 h-5 text-white/60" />
              </div>
              <p className="text-4xl font-bold text-white">{dashboardStats.activeRoutes}</p>
            </div>

            <div className="p-6 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white/80 text-sm font-semibold">On-Time Rate</p>
                <CheckCircle className="w-5 h-5 text-white/60" />
              </div>
              <p className="text-4xl font-bold text-white">{dashboardStats.onTimeRate}%</p>
            </div>
          </div>

          {/* Main Dashboard Panel */}
          <div className="lg:col-span-2 p-8 rounded-3xl backdrop-blur-xl bg-white/95 border border-white/50 shadow-2xl">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-2xl font-bold text-[#0B0F14]">Live Ride Tracking</h3>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#2ECC71]/20">
                <div className="w-2 h-2 rounded-full bg-[#2ECC71] animate-pulse"></div>
                <span className="text-sm font-semibold text-[#2ECC71]">Live</span>
              </div>
            </div>

            {/* Ride List */}
            <div className="space-y-3 mb-6">
              {mockRides.map((ride) => (
                <div
                  key={ride.id}
                  onClick={() => setSelectedRide(ride)}
                  className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                    selectedRide.id === ride.id
                      ? 'border-[#0077BE] bg-[#0077BE]/5'
                      : 'border-gray-200 hover:border-[#00B4D8] bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(ride.status)}`}></div>
                      <div>
                        <p className="font-bold text-[#0B0F14]">{ride.route}</p>
                        <p className="text-sm text-[#5F6B7A]">{ride.id}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(ride.status)}`}>
                        {getStatusText(ride.status)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Ride Details */}
            {selectedRide && (
              <div className="p-6 rounded-2xl bg-gradient-to-br from-[#F7F9FB] to-white border border-gray-200">
                <h4 className="font-bold text-lg text-[#0B0F14] mb-4">Ride Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-[#0077BE]" />
                    <div>
                      <p className="text-xs text-[#5F6B7A]">Driver</p>
                      <p className="font-semibold text-[#0B0F14]">{selectedRide.driver}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#0077BE]" />
                    <div>
                      <p className="text-xs text-[#5F6B7A]">Pickup Time</p>
                      <p className="font-semibold text-[#0B0F14]">{selectedRide.pickup}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Navigation className="w-5 h-5 text-[#0077BE]" />
                    <div>
                      <p className="text-xs text-[#5F6B7A]">Status</p>
                      <p className="font-semibold text-[#0B0F14]">{getStatusText(selectedRide.status)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-[#0077BE]" />
                    <div>
                      <p className="text-xs text-[#5F6B7A]">
                        {selectedRide.status === 'completed' ? 'Dropoff' : 'ETA'}
                      </p>
                      <p className="font-semibold text-[#0B0F14]">
                        {selectedRide.dropoff || selectedRide.eta || selectedRide.scheduled}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 text-center">
              <Button 
                onClick={() => setShowDemoModal(true)}
                className="bg-gradient-to-r from-[#0077BE] to-[#00B4D8] text-white hover:shadow-xl transition-all duration-300 hover:scale-105 px-8 py-6 rounded-2xl text-lg font-semibold"
              >
                Request Demo
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Demo Modal */}
      <DemoRequestModal 
        open={showDemoModal} 
        onOpenChange={setShowDemoModal}
      />
    </section>
  );
};

export default AppPreviewSection;
